import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  Tooltip,
} from '@material-ui/core';
import { setSpecificSpecialEventData } from '../../../redux/actions/newOperationHoursActions';
import { formatTime } from '../../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    root: {
      flexGrow: 1,
      borderBottom: '1px solid',
      borderBottomColor: '#bdbdbd',
      marginTop: 5,
    },
    demo: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

// eslint-disable-next-line import/no-anonymous-default-export
export default function RowDriveThru({ disableAll, restaurantId = '' }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const {
    dtTimeOpenMode,
    dtTimeCloseMode,
    dtIsClosedMode,
    dtIsOpened24HoursMode,
    dtIsOpened24Hours,
    digitalDtIsOpened24Hours,
    dtIsClosed,
    digitalDtIsClosed,
    dtTimeOpen,
    dtTimeClose,
    drIsOpened24Hours,
    drIsClosed,
    availableTimes,
    is12HFormat,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);
  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );
  const digitalDriveThruService = restaurantServices.find(
    elem => elem.code === 'digitalDriveThru',
  );

  const [selectOpenSelectedIndex, setSelectOpenSelectedIndex] = useState(0);

  const disabledSelects =
    disableAll || dtIsClosed === 'Y' || dtIsOpened24Hours === 'Y';

  const getValueSelectOpen = () => {
    if (dtIsClosed === 'Y') {
      return '';
    } else if (dtIsOpened24Hours === 'Y') {
      return availableTimes[1];
    } else {
      return dtTimeOpen ? dtTimeOpen : '';
    }
  };

  const getValueSelectClose = () => {
    if (dtIsClosed === 'Y') {
      return '';
    } else if (dtIsOpened24Hours === 'Y') {
      return '23:59';
    } else {
      return dtTimeClose ? dtTimeClose : '';
    }
  };

  const onChangeSelectOpen = event => {
    const select = event.target as HTMLSelectElement;
    if (dtTimeOpen !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          dtTimeOpen: select.value,
        }),
      );
    }
  };

  const onChangeSelectClose = event => {
    const select = event.target as HTMLSelectElement;
    if (dtTimeClose !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          dtTimeClose: select.value,
        }),
      );
    }
  };

  const onChangeCheckbox24hours = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.dtIsOpened24Hours = 'Y';
      newState.dtIsClosed = 'N';
      newState.closeToAll = false;

      if (drIsOpened24Hours === 'Y' && hasOpenDriveThru()) {
        newState.open24Hours = true;
      }
    } else {
      newState.dtIsOpened24Hours = 'N';
      newState.open24Hours = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  const onChangeCheckboxIsClosed = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.dtIsClosed = 'Y';
      newState.dtIsOpened24Hours = 'N';
      newState.open24Hours = false;

      if (drIsClosed === 'Y' && hasCloseDriveThru()) {
        newState.closeToAll = true;
      }
    } else {
      newState.dtIsClosed = 'N';
      newState.closeToAll = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  useEffect(() => {
    const index = availableTimes.findIndex(elem => elem === dtTimeOpen);
    setSelectOpenSelectedIndex(index);
  }, [dtTimeOpen]);

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={4} md={4}>
          <div className={classes.demo}>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('operationHours.driveThru')}
                  primaryTypographyProps={{ display: 'inline' }}
                  secondary={!restaurantId ? '*' : ''}
                  secondaryTypographyProps={
                    !restaurantId
                      ? { display: 'inline', color: 'error', variant: 'h5' }
                      : undefined
                  }
                />
              </ListItem>
            </List>
          </div>
        </Grid>
        <Grid item xs={1} md={1}>
          {dtIsOpened24HoursMode.includes('read') ? (
            <Checkbox
              style={{ marginTop: 10 }}
              disabled={disableAll || !dtIsOpened24HoursMode.includes('write')}
              checked={dtIsOpened24Hours === 'Y'}
              onChange={onChangeCheckbox24hours}
            />
          ) : null}
        </Grid>
        <Grid item xs={1} md={1}>
          {dtIsClosedMode.includes('read') ? (
            <Checkbox
              style={{ marginTop: 10 }}
              disabled={disableAll || !dtIsClosedMode.includes('write')}
              checked={dtIsClosed === 'Y'}
              onChange={onChangeCheckboxIsClosed}
            />
          ) : null}
        </Grid>
        <Grid item xs={3} md={3}>
          {dtTimeOpenMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.start')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !dtTimeOpenMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectOpen()}
                  label={t('operationHours.start')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectOpen}
                  disabled={
                    disabledSelects || !dtTimeOpenMode.includes('write')
                  }
                >
                  {availableTimes?.map((elem, index) => (
                    <option key={index} value={elem}>
                      {formatTime(elem, is12HFormat)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
        <Grid item xs={3} md={3}>
          {dtTimeCloseMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.close')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !dtTimeCloseMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectClose()}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectClose}
                  disabled={
                    disabledSelects || !dtTimeCloseMode.includes('write')
                  }
                >
                  {dtIsOpened24Hours === 'Y' ? (
                    <option value={'23:59'}>
                      {formatTime('23:59', is12HFormat)}
                    </option>
                  ) : (
                    availableTimes?.map((elem, index) => (
                      <option key={index} value={elem}>
                        {formatTime(elem, is12HFormat)}
                      </option>
                    ))
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );

  function isDigitalDriveThruOpened24Hours() {
    return digitalDtIsOpened24Hours === 'Y';
  }

  function isDigitalDriveThruClosed() {
    return digitalDtIsClosed === 'Y';
  }

  function hasDigitalDriveThru() {
    return brand === 'BK' && digitalDriveThruService?.value;
  }

  function hasOpenDriveThru() {
    return !hasDigitalDriveThru() || isDigitalDriveThruOpened24Hours();
  }

  function hasCloseDriveThru() {
    return !hasDigitalDriveThru() || isDigitalDriveThruClosed();
  }
}

RowDriveThru.displayName = 'RowDriveThru';
